<template>
  <transition name="maintenance-fade">
    <div v-if="visible" class="maintenance-overlay" :class="{ 'dark-theme': isDark }">
      <div class="maintenance-card">
        <div class="maintenance-icon">
          <div class="icon-ring">
            <i class="fa-solid fa-server"></i>
          </div>
          <div class="pulse-ring"></div>
        </div>
        <h1 class="maintenance-title">服务器维护中</h1>
        <p class="maintenance-desc">服务器正在维护或暂时无法连接，请稍后再试</p>
        <div class="maintenance-status">
          <span class="status-dot"></span>
          <span>正在尝试重新连接...</span>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'ServerMaintenanceOverlay',
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isDark: false,
      observer: null
    }
  },
  mounted() {
    this.checkTheme()
    if (typeof MutationObserver !== 'undefined') {
      this.observer = new MutationObserver(() => this.checkTheme())
      this.observer.observe(document.body, { attributes: true, attributeFilter: ['class'] })
      this.observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })
    }
  },
  beforeUnmount() {
    if (this.observer) {
      this.observer.disconnect()
    }
  },
  methods: {
    checkTheme() {
      const bodyHas = typeof document !== 'undefined' && document.body && document.body.classList.contains('dark-theme')
      const htmlHas = typeof document !== 'undefined' && document.documentElement && document.documentElement.classList.contains('dark-theme')
      this.isDark = !!(bodyHas || htmlHas)
    }
  }
}
</script>

<style scoped>
.maintenance-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: var(--z-critical);
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(245, 245, 247, 0.92);
  backdrop-filter: blur(30px) saturate(180%);
  -webkit-backdrop-filter: blur(30px) saturate(180%);
}

.maintenance-card {
  text-align: center;
  padding: 60px 48px;
  max-width: 460px;
  width: 90%;
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.75);
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.08), 0 0 0 0.5px rgba(0, 0, 0, 0.04);
}

.maintenance-icon {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 32px;
}

.icon-ring {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f0f0f3, #e8e8ed);
  border: 1px solid rgba(0, 0, 0, 0.06);
  font-size: 32px;
  color: #86868b;
  position: relative;
  z-index: 1;
}

.pulse-ring {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 80px;
  height: 80px;
  border-radius: 50%;
  border: 2px solid rgba(142, 142, 147, 0.3);
  animation: pulse 2s ease-out infinite;
}

@keyframes pulse {
  0% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 0.6;
  }
  100% {
    transform: translate(-50%, -50%) scale(1.8);
    opacity: 0;
  }
}

.maintenance-title {
  font-size: 28px;
  font-weight: 700;
  color: #1d1d1f;
  margin: 0 0 12px 0;
  letter-spacing: -0.02em;
}

.maintenance-desc {
  font-size: 16px;
  color: #86868b;
  line-height: 1.6;
  margin: 0 0 28px 0;
}

.maintenance-status {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #86868b;
  padding: 8px 18px;
  border-radius: 100px;
  background: rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(0, 0, 0, 0.04);
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #ff9500;
  animation: blink 1.5s ease-in-out infinite;
}

@keyframes blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
}

.maintenance-fade-enter-active,
.maintenance-fade-leave-active {
  transition: opacity 0.5s ease;
}

.maintenance-fade-enter-from,
.maintenance-fade-leave-to {
  opacity: 0;
}

/* 深色模式完整兼容样式 */
:global(body.dark-theme) .maintenance-overlay,
:global(html.dark-theme) .maintenance-overlay,
.maintenance-overlay.dark-theme {
  background: rgba(28, 28, 30, 0.94);
}

:global(body.dark-theme) .maintenance-card,
:global(html.dark-theme) .maintenance-card,
.maintenance-overlay.dark-theme .maintenance-card {
  background: rgba(44, 44, 46, 0.85);
  border-color: rgba(255, 255, 255, 0.08);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4), 0 0 0 0.5px rgba(255, 255, 255, 0.06);
}

:global(body.dark-theme) .icon-ring,
:global(html.dark-theme) .icon-ring,
.maintenance-overlay.dark-theme .icon-ring {
  background: linear-gradient(135deg, #3a3a3c, #2c2c2e);
  border-color: rgba(255, 255, 255, 0.08);
  color: #98989d;
}

:global(body.dark-theme) .pulse-ring,
:global(html.dark-theme) .pulse-ring,
.maintenance-overlay.dark-theme .pulse-ring {
  border-color: rgba(174, 174, 178, 0.2);
}

:global(body.dark-theme) .maintenance-title,
:global(html.dark-theme) .maintenance-title,
.maintenance-overlay.dark-theme .maintenance-title {
  color: #f5f5f7;
}

:global(body.dark-theme) .maintenance-desc,
:global(html.dark-theme) .maintenance-desc,
.maintenance-overlay.dark-theme .maintenance-desc {
  color: #98989d;
}

:global(body.dark-theme) .maintenance-status,
:global(html.dark-theme) .maintenance-status,
.maintenance-overlay.dark-theme .maintenance-status {
  color: #98989d;
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.06);
}
</style>
